import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { MaxsoldFavouriteDto, MaxsoldFavouritesDto, MaxsoldStatusDto } from '@jordan/types';
import { Alert, Breadcrumb, Button, DataTable, PageHeader, Spinner } from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { ApiError, apiDelete, apiGet } from '../../lib/api';
import { maxsoldSidebar } from './maxsoldSidebar';
import { ListingGrid, listingColumns, type ListingView } from './MaxsoldListing';

/** Bid-status badge — watchlist-only lots get none (the filled heart already conveys "favourited"). */
function statusBadge(f: MaxsoldFavouriteDto): ListingView['badge'] {
  if (f.isWinning) return { label: 'Winning', variant: 'success' };
  if (f.userHasBid) return { label: 'Outbid', variant: 'danger' };
  return null;
}

export function MaxsoldFavouritesPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [status, setStatus] = useState<MaxsoldStatusDto | null>(null);
  const [data, setData] = useState<MaxsoldFavouritesDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  // Ticks once a minute so "time left" stays current without a manual refresh.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    apiGet<MaxsoldStatusDto>('/api/maxsold/status')
      .then(setStatus)
      .catch(() => setErr('Failed to load Maxsold status.'));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setErr(null);
    apiGet<MaxsoldFavouritesDto>('/api/maxsold/favourites')
      .then(setData)
      .catch((e: unknown) => {
        const body = e instanceof ApiError ? (e.body as { message?: string } | null) : null;
        setErr(body?.message ?? 'Could not load your favourites.');
        setData(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (status?.configured) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.configured]);

  const openLot = useCallback((id: number) => navigate(`/maxsold/lot/${id}?from=favourites`), [navigate]);

  // Un-favourite: drop the lot from the list immediately, then persist. Refetch to resync on failure.
  const removeFav = useCallback(
    (id: number) => {
      setData((prev) => (prev ? { total: Math.max(0, prev.total - 1), items: prev.items.filter((i) => i.id !== id) } : prev));
      apiDelete(`/api/maxsold/watchlist/${id}`).catch(() => {
        setErr('Could not remove that favourite — refreshing.');
        load();
      });
    },
    [load],
  );

  const kw = query.trim().toLowerCase();
  const views = useMemo<ListingView[]>(
    () =>
      (data?.items ?? [])
        .filter(
          (f) => kw === '' || `${f.title} ${f.auctionTitle ?? ''} ${f.lotNumber ?? ''}`.toLowerCase().includes(kw),
        )
        .map((f) => ({
          id: f.id,
          imageUrl: f.imageUrl,
          title: f.title,
          subtitle: `${f.auctionTitle ?? `Auction ${f.auctionId}`}${f.lotNumber ? ` · Lot ${f.lotNumber}` : ''}`,
          badge: statusBadge(f),
          price: f.currentBid,
          secondaryPrice: f.proxyBid,
          bidCount: f.bidCount,
          closeTime: f.endTime,
          favourite: { active: true, pending: false, onToggle: () => removeFav(f.id) },
        })),
    [data, kw, removeFav],
  );

  const columns = useMemo(
    () => listingColumns({ now, price: true, secondaryPrice: true, bids: true, favourite: true }),
    [now],
  );

  return (
    <AppChrome secondarySidebar={maxsoldSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Maxsold - <span className="fw-normal">Favourites</span>
          </>
        }
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Maxsold' }, { label: 'Favourites' }]} />}
      />

      <div className="content">
        {status && !status.configured && (
          <Alert variant="info" icon="ph-info">
            Maxsold isn&apos;t connected. Add <code>MAXSOLD_EMAIL</code> and <code>MAXSOLD_PASSWORD</code> to the server{' '}
            <code>.env</code> to view your favourites.
          </Alert>
        )}
        {err && (
          <Alert variant="danger" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        {status?.configured && (
          <div className="card">
            <div className="card-header d-flex flex-wrap align-items-center gap-2">
              <h5 className="mb-0 me-auto">Favourites{data ? <span className="text-muted fw-normal ms-2">{data.total} items</span> : null}</h5>
              <div className="form-control-feedback form-control-feedback-start" style={{ maxWidth: 240 }}>
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <div className="form-control-feedback-icon">
                  <i className="ph-magnifying-glass opacity-50" />
                </div>
              </div>
              <div className="btn-group" role="group" aria-label="View">
                {(['grid', 'list'] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    className={`btn btn-sm ${view === v ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => setView(v)}
                    title={v}
                  >
                    <i className={v === 'grid' ? 'ph-squares-four' : 'ph-list'} />
                  </button>
                ))}
              </div>
              <Button variant="light" icon="ph-arrows-clockwise" disabled={loading} onClick={load}>
                Refresh
              </Button>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner />
                </div>
              ) : view === 'grid' ? (
                <ListingGrid
                  views={views}
                  now={now}
                  onOpen={openLot}
                  emptyMessage="No favourited lots."
                  surchargePercent={status?.surchargePercent ?? 0}
                />
              ) : (
                <DataTable
                  columns={columns}
                  rows={views}
                  getRowKey={(v) => String(v.id)}
                  onRowClick={(v) => openLot(v.id)}
                  pageSize={status?.favouritesPageSize ?? 0}
                  initialSort={{ key: 'closeTime', dir: 'asc' }}
                  emptyMessage="No favourited lots."
                />
              )}
            </div>
          </div>
        )}
      </div>
    </AppChrome>
  );
}
